import {
  ApiError,
  ApiErrorResponse,
  ClassifierMappingExcelUrl,
  ClassifierMappingsType,
  GetApiErrorMessage,
  excelTranslations,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import { expand } from 'uri-template-lite';
import { ClassifierMappingUpload } from './ClassifierMappingUpload';
import { ReactNode, useCallback } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierMappingExcelActions.translations.json';
import { useFormContext } from 'react-hook-form';

const { addTranslations, t } = i18nNsFactory(
  'classifier-mapping-excel-actions',
);
addTranslations(translations);
addTranslations(excelTranslations);

export interface ClassifierMappingExcelActionsProps {
  edit?: boolean;
  id?: number;
  onError?: (
    apiErrors: ApiError[],
    getErrorMessage?: GetApiErrorMessage,
  ) => void;
  onSuccess?: () => void;
  readOnly?: boolean;
}

export function ClassifierMappingExcelActions({
  id,
  edit,
  onError,
  onSuccess,
  readOnly,
}: Readonly<ClassifierMappingExcelActionsProps>) {
  const form = useFormContext<ClassifierMappingsType>();

  const getErrorMessage: GetApiErrorMessage = useCallback(
    (code, meta) =>
      t(`alert.error.code.${code}`, meta?.args && { ...meta.args }),
    [],
  );

  const handleUploadError = useCallback(
    (error: ApiErrorResponse) => {
      onError?.(error.response?.data.errors ?? [], getErrorMessage);
    },
    [getErrorMessage, onError],
  );

  const handleUploadSuccess = useCallback(
    (responseData: Partial<ClassifierMappingsType>) => {
      const current = form.getValues();
      if (id) {
        // Existing mapping basic data cannot be changed
        if (
          current.classifierIdTo !== responseData.classifierIdTo ||
          current.classifierIdFrom !== responseData.classifierIdFrom
        ) {
          onError?.([{ code: 'mapping-changed' }], getErrorMessage);
          return;
        }
        if (readOnly && current.code !== responseData.code) {
          onError?.([{ code: 'code-changed' }], getErrorMessage);
          return;
        }
      }
      form.reset({
        ...current,
        ...responseData,
        id,
      } as ClassifierMappingsType);
      onSuccess?.();
    },
    [getErrorMessage, form, id, onError, onSuccess, readOnly],
  );

  let upload: ReactNode | null = null;
  if (edit) {
    upload = (
      <ClassifierMappingUpload
        onError={handleUploadError}
        onSuccess={handleUploadSuccess}
        showWarning={!!id}
      />
    );
  }
  let download: ReactNode | null = null;
  if (id) {
    download = (
      <span>
        <Button
          id="download-classifierMapping"
          icon={<Icon.Download />}
          href={expand(ClassifierMappingExcelUrl, { id })}
        />
      </span>
    );
  }
  return (
    <Flex gap="small">
      {upload}
      {download}
    </Flex>
  );
}
